import {
  AddProductInFavoriteAction, AddProductInListAction,
  ClearFoundActionSuccess, DeleteOneProductFromListAction,
  DeleteProductsFromListAction, ResetPageCountLoadDataAction
} from '@actions';
import { AfterViewInit, ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { AppState } from '@appStates';
import { LocalizationService, ScreenDetectedService } from '@core/services';
import { BzProduct, Catalog, IBanner, ICatalogProducts, ICatalogTextWithEditor, IEditor, ITableCatalogProductsResponse } from '@interfaces';
import { Store } from '@ngrx/store';
import { Subject, forkJoin, fromEvent, of } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { JsonLDServiceService } from '../../core/services/seo.services/json-ldservice.service';
import { ScrollPositionLocalStorageService } from '../../core/services/storage.services/scroll-position-local-storage.service';
import { GeoLocationService } from 'src/app/core/services/positioning.services/geo-location.service';
import { CatalogProductsService } from 'src/app/core/services/api.services/catalog-products.service';
import { Actions, ofType } from '@ngrx/effects';
import { RestUrlService, TypeUrl } from 'src/app/core/services/api.services/settings/rest-url.service';

@Component({
  selector: 'app-catalog',
  templateUrl: './catalog.component.html',
  styleUrls: ['./catalog.component.sass']
})
export class CatalogComponent implements OnInit, AfterViewInit, OnDestroy {

  @HostBinding('class.scrollbar') public addScrollbar = false;

  public catalog: Catalog;

  public h1: { seoH1: string, originH1: string } = { seoH1: null, originH1: null };

  public bufferCatalog: string | null;

  public groups: ICatalogProducts[] = [];

  public table: ITableCatalogProductsResponse | null = null;

  public text: string | null = null;

  public editor: IEditor | null = null;

  public adBanner: IBanner | null = null;

  public bannerImages: string;

  public lang: string;

  public showFullSeoText = false;

  private readonly debounceTime: number = 250;

  private componentDestroyer: Subject<void> = new Subject();

  trackById = (_: unknown, item: { id: number | string }) => item.id;

  get currentYear(): number {
    return new Date().getFullYear();
  }

  constructor(
    private readonly location: Location,
    private readonly store: Store<AppState>,
    private readonly locale: LocalizationService,
    private readonly spls: ScrollPositionLocalStorageService,
    private readonly active: ActivatedRoute,
    private readonly jld: JsonLDServiceService,
    private readonly screenDetectedService: ScreenDetectedService,
    private readonly cdr: ChangeDetectorRef,
    private readonly geoLocation: GeoLocationService,
    private readonly catalogProductsService: CatalogProductsService,
    private readonly actions: Actions,
    private readonly router: Router,
    private readonly restUrlService: RestUrlService) {
    this.bannerImages = this.restUrlService.getUrl(TypeUrl.img) + '/ad/';
    this.lang = this.locale.getLang();
    this.active.data
      .pipe(
        map((data: Data) => {
          return data[0];
        }),
        takeUntil(this.componentDestroyer)
      ).subscribe((guardData: {
        value: Catalog,
        h1: { seoH1: string, originH1: string },
        groups: ICatalogProducts[],
        table: ITableCatalogProductsResponse | null,
        textWithEditor: ICatalogTextWithEditor | null,
      }) => {
        this.h1 = guardData.h1;
        this.bufferCatalog = guardData.value.url || null;
        this.catalog = guardData.value;
        this.groups = guardData.groups;
        this.table = guardData.table;
        this.text = guardData.textWithEditor?.text ?? '';
        this.editor = guardData.textWithEditor?.editor || null;
        this.adBanner = guardData.textWithEditor?.adBanner || null;
      });
  }

  ngOnInit(): void {
    this.addScrollbar = true;
    this.spls.scrollPosition = '0';
    this.initCityChangeListener();
    this.initAddOrRemoveProductToListListener();
    this.initAddOrRemoveProductToFavoriteListener();
    this.initRemoveProductFromListListener();
    this.initClearProductsFromListListener();
    this.initWindowResizeListener();
    this.initPageChangeListener();
  }

  ngAfterViewInit(): void {
    this.checkIfNeedToShowArrows();
    this.cdr.detectChanges();
  }

  public getHomePageLink(): string {
    return this.locale.getLang() === 'ru' ? '/' : '/ua';
  }

  public pushBtnBack(): void {
    this.location.back();
  }

  public toSecondRouterLinkLevelOfCatalog(child: any): string {

    if (child.info === '0') {
      return this.locale.getRouterLinkApp('') + '/' + child.url.toLowerCase();
    }
    if (child.info === '3') {
      return this.locale.getRouterLinkApp('category') + '/' + child.url;
    } else {
      return this.locale.getRouterLinkApp('catalog') + '/' + child.url;
    }
  }

  public resetCategoryPage(groupId?: number): void {
    this.scrollToTopWhenNavToSecondLvl(groupId);
    this.store.dispatch(ClearFoundActionSuccess());
    this.store.dispatch(ResetPageCountLoadDataAction());
  }

  public getLinkForMore(id: number): string {
    const section = this.catalog.children.find(x => x.id === id);
    if (section.info === '3') {
      return this.locale.getRouterLinkApp('category') + '/' + section.url;
    } else {
      return this.locale.getRouterLinkApp('catalog') + '/' + section.url;
    }
  }

  public scrollProductsContainer(direction: string, groupName: string): void {
    const clickedContainer = document.getElementById(`single-group__bottom-${groupName}`);
    if (direction === 'right') {
      clickedContainer.scrollTo({
        left: clickedContainer.scrollLeft += 150,
        behavior: 'smooth'
      });
    }
    if (direction === 'left') {
      clickedContainer.scrollTo({
        left: clickedContainer.scrollLeft -= 150,
        behavior: 'smooth'
      });
    }

    setTimeout(() => {
      const scrollLeft = clickedContainer.scrollLeft;
      const clientWidth = clickedContainer.clientWidth;
      const scrollWidth = clickedContainer.scrollWidth;

      const leftArrowContainer = document.getElementById(`left-arrow-${groupName}`);
      const rightArrowContainer = document.getElementById(`right-arrow-${groupName}`);

      if (scrollLeft > 0) {
        leftArrowContainer.style.display = 'flex';
      } else {
        leftArrowContainer.style.display = 'none';
      }
      if (scrollLeft + clientWidth === scrollWidth) {
        rightArrowContainer.style.display = 'none';
      } else {
        rightArrowContainer.style.display = 'flex';
      }
    }, this.debounceTime);
  }

  public readMoreSeoText(): void {
    this.showFullSeoText = !this.showFullSeoText;
  }

  private initCityChangeListener(): void {
    this.geoLocation.catalogCityChangeEmitter.pipe(
      map((url: string): [string, boolean] => [url, this.screenDetectedService.isDesktop()]),
      switchMap(([url, isDesktop]: [string, boolean]) => forkJoin([
        isDesktop ? this.catalogProductsService.getCatalogProducts(url).pipe(take(1)) : of([]),
        this.catalogProductsService.getCatalogProductsTable(url).pipe(take(1)),
        this.catalogProductsService.getCatalogProductsTextWithEditor(url).pipe(take(1)),
      ])),
      takeUntil(this.componentDestroyer)
    ).subscribe(([productGroups, table, textWithEditor]) => {
      this.checkIfNeedToShowArrows();
      this.groups = productGroups;
      this.table = table;
      this.text = textWithEditor.text;
      this.editor = textWithEditor.editor;
      this.adBanner = textWithEditor.adBanner;
    });
  }

  private initAddOrRemoveProductToListListener(): void {
    this.actions.pipe(
      ofType(AddProductInListAction),
      takeUntil(this.componentDestroyer)
    ).subscribe(({ product }: { product: BzProduct['id'] }) => {
      this.groups = this.groups.map((group) => {
        const newProducts = group.products.map((p) => {
          if (p.id === product) {
            p = { ...p, inList: !p.inList };
          }
          return p;
        });
        return { ...group, products: newProducts };
      });
    });
  }

  private initAddOrRemoveProductToFavoriteListener(): void {
    this.actions.pipe(
      ofType(AddProductInFavoriteAction),
      takeUntil(this.componentDestroyer)
    ).subscribe(({ id }: { id: BzProduct['id'] }) => {
      this.groups = this.groups.map((group) => {
        const newProducts = group.products.map((p) => {
          if (p.id === id) {
            p = { ...p, isFavorite: !p.isFavorite };
          }
          return p;
        });
        return { ...group, products: newProducts };
      });
    });
  }

  private initRemoveProductFromListListener(): void {
    this.actions.pipe(
      ofType(DeleteOneProductFromListAction),
      takeUntil(this.componentDestroyer)
    ).subscribe(({ product }: { product: BzProduct['id'] }) => {
      this.groups = this.groups.map((group) => {
        const newProducts = group.products.map((p) => {
          if (p.id === product) {
            p = { ...p, inList: false };
          }
          return p;
        });
        return { ...group, products: newProducts };
      });
    });
  }

  private initClearProductsFromListListener(): void {
    this.actions.pipe(
      ofType(DeleteProductsFromListAction),
      takeUntil(this.componentDestroyer)
    ).subscribe(() => {
      this.groups = this.groups.map((group) => {
        const newProducts = group.products.map((p) => {
          return { ...p, inList: false };
        });
        return { ...group, products: newProducts };
      });
    });
  }

  private initWindowResizeListener(): void {
    fromEvent(window, 'resize').pipe(
      debounceTime(this.debounceTime),
      takeUntil(this.componentDestroyer)
    ).subscribe(() => {
      this.checkIfNeedToShowArrows();
    });
  }

  private initPageChangeListener(): void {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      debounceTime(this.debounceTime),
      takeUntil(this.componentDestroyer)
    ).subscribe(() => this.checkIfNeedToShowArrows());
  }

  private scrollToTopWhenNavToSecondLvl(groupId: number | undefined): void {
    if (groupId) {
      const section = this.catalog.children.find(x => x.id === groupId);
      if (section.info !== '3') {
        const container = document.getElementsByTagName('app-catalog')[0];
        if (container) {
          container.scroll({ top: 0 });
        }
      }
    }
  }

  private checkIfNeedToShowArrows(): void {
    this.groups.forEach((group) => {
      setTimeout(() => {
        const productsContainer = document.getElementById(`single-group__bottom-${group.name_short}`);
        if (productsContainer) {
          const scrollLeft = productsContainer.scrollLeft;
          const clientWidth = productsContainer.clientWidth;
          const scrollWidth = productsContainer.scrollWidth;

          const rightArrowContainer = document.getElementById(`right-arrow-${group.name_short}`);
          const leftArrowContainer = document.getElementById(`left-arrow-${group.name_short}`);

          if (clientWidth === scrollWidth) {
            rightArrowContainer.style.display = 'none';
          }
          if (clientWidth < scrollWidth) {
            rightArrowContainer.style.display = 'flex';
          }
          if (scrollLeft > 0) {
            leftArrowContainer.style.display = 'flex';
          } else {
            leftArrowContainer.style.display = 'none';
          }
        }
      }, this.debounceTime);
    });
  }

  ngOnDestroy(): void {
    if (this.screenDetectedService.isMobile()) {
      this.store.dispatch(ClearFoundActionSuccess());
    }
    this.jld.remove();
    this.componentDestroyer.next(null);
    this.componentDestroyer.complete();
  }

}
